// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import LazyImage, { LazyImageProps } from "@bg/MobiusOverrides/LazyImage";
import ProductAddToCartButton, { productAddToCartButtonStyles } from "@bgs/Overrides/Components/ProductAddToCartButton";
import ProductQuantityOrdered, { productQuantityOrderedStyles } from "@bgs/Overrides/Components/ProductQuantityOrdered";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import clearProduct from "@insite/client-framework/Store/Components/ProductSelector/Handlers/ClearProduct";
import reset from "@insite/client-framework/Store/Components/ProductSelector/Handlers/Reset";
import searchProducts from "@insite/client-framework/Store/Components/ProductSelector/Handlers/SearchProducts";
import setProduct from "@insite/client-framework/Store/Components/ProductSelector/Handlers/SetProduct";
import setUnitOfMeasure from "@insite/client-framework/Store/Components/ProductSelector/Handlers/SetUnitOfMeasure";
import { getProductSelector } from "@insite/client-framework/Store/Components/ProductSelector/ProductSelectorSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { SelectPresentationProps } from "@insite/mobius/Select";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import debounce from "lodash/debounce";
import React, { ChangeEvent, ReactNode, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    onSelectProduct: (productInfo: ProductInfo, product: ProductModel) => void;
    skipInventoryValidation?: boolean;
    selectButtonTitle?: string;
    productIsConfigurableMessage?: ReactNode;
    productIsUnavailableMessage?: ReactNode;
    customErrorMessage?: ReactNode;
    extendedStyles?: ProductSelectorStyles;
    showAddToCart?: boolean;
}

const mapStateToProps = (state: ApplicationState) => {
    const { isSearching, searchResults, selectedProductInfo, errorType } = getProductSelector(state);
    const product = getProductState(state, selectedProductInfo?.productId).value;
    return {
        isSearching,
        searchResults,
        selectedProductInfo,
        product,
        errorType,
        location: getLocation(state),
    };
};

const mapDispatchToProps = {
    searchProducts,
    setProduct,
    clearProduct,
    setUnitOfMeasure,
    reset,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductSelectorStyles {
    container?: InjectableCss;
    searchWrapper?: InjectableCss;
    qtyWrapper?: InjectableCss;
    unitOfMeasureWrapper?: InjectableCss;
    buttonWrapper?: InjectableCss;
    unitOfMeasureSelect?: SelectPresentationProps;
    optionWrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    productImage?: LazyImageProps;
    infoWrapper?: InjectableCss;
    autocompleteTitleText?: TypographyPresentationProps;
    autocompleteErpText?: TypographyPresentationProps;
    selectButton?: ButtonPresentationProps;
    searchDynamicDropdown?: DynamicDropdownPresentationProps;
}

export const productSelectorStyles: ProductSelectorStyles = {
    container: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            padding: 20px 0px;
            width: 100%;
        `,
    },
    searchWrapper: {
        css: css`
            flex: 1 1 300px;
            padding-right: 15px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex: 1 1 100%;
                        padding: 0;
                        margin-bottom: 15px;
                        margin-right: 0 !important;
                    `,
                    css`
                        flex: 1 1 100%;
                        padding: 0;
                        margin-bottom: 15px;
                    `,
                    null,
                    null,
                    null,
                ])}

            @media (max-width: 1200px) and (min-width: 769px) {
                margin-bottom: 15px;
            }
        `,
    },
    qtyWrapper: {
        css: css`
            width: 139px;
            padding-right: 15px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex: 0 1 50%;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
            label {
                line-height: 18px;
                font-weight: 400;
                font-size: 15px;
            }
        `,
    },
    unitOfMeasureWrapper: {
        css: css`
            width: 135px;
            padding-right: 15px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex: 0 1 50%;
                        padding: 0;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    buttonWrapper: {
        css: css`
            display: flex;
            align-items: flex-start;

            button {
                margin-top: 25px !important;
                max-height: 37px !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex: 0 1 100%;
                        margin-top: 5px;
                        button {
                            width: 100%;
                        }
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    optionWrapper: {
        css: css`
            display: flex;
            cursor: pointer;
        `,
    },
    imageWrapper: {
        css: css`
            display: flex;
            width: 50px;
            height: 50px;
            margin-right: 10px;
        `,
    },
    productImage: {
        width: "50px",
        cloudinaryWidth: "50",
        disableLazyLoad: true,
        css: css`
            flex-shrink: 0;
            img {
                height: 100%;
            }
        `,
    },
    infoWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    autocompleteTitleText: {
        size: 14,
    },
    autocompleteErpText: {
        size: 14,
        css: css`
            margin-top: 5px;
        `,
    },
};

const ENTER_KEY = 13;

const regexp = /^[0-9\b]+$/;

const isValidNumberic = (number: string) => {
    if (number === "") {
        // backspace or delete case
        return true;
    }
    return regexp.test(number);
};

const ProductSelector = ({
    onSelectProduct,
    skipInventoryValidation,
    selectButtonTitle,
    searchProducts,
    isSearching,
    searchResults,
    setProduct,
    clearProduct,
    setUnitOfMeasure,
    selectedProductInfo,
    product,
    errorType,
    extendedStyles,
    customErrorMessage,
    productIsConfigurableMessage,
    productIsUnavailableMessage,
    location,
    reset,
    showAddToCart,
}: Props) => {
    const [qty, setQty] = useState("1");
    const [qtyHasChanged, setQtyHasChanged] = useState(false);
    const [errorMessage, setErrorMessage] = useState<ReactNode>("");
    const [options, setOptions] = useState<OptionObject[]>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [styles] = useState(() => mergeToNew(productSelectorStyles, extendedStyles));
    // Xng-Change
    const [selectedProductId, setSelectedProductId] = useState("");

    useEffect(() => {
        reset();
    }, [location.pathname]);

    useEffect(() => {
        if (!searchResults) {
            return;
        }

        const newOptions = searchResults.map(product => ({
            optionText: product.title,
            optionValue: product.id || undefined,
            rowChildren: (
                <StyledWrapper
                    key={`productSelector_${product.id}`}
                    {...styles.optionWrapper}
                    data-test-selector={`productSelector_search_${product.id}`}
                >
                    <StyledWrapper {...styles.imageWrapper}>
                        <LazyImage
                            {...styles.productImage}
                            altText={product.title || product.name}
                            src={product.image}
                        />
                    </StyledWrapper>
                    <StyledWrapper {...styles.infoWrapper}>
                        <Typography {...styles.autocompleteTitleText}>{product.title}</Typography>
                        <Typography {...styles.autocompleteErpText}>{product.erpNumber}</Typography>
                    </StyledWrapper>
                </StyledWrapper>
            ),
        }));
        setOptions(newOptions);
    }, [searchResults]);

    useEffect(() => {
        setErrorMessage(customErrorMessage);
    }, [customErrorMessage]);

    useEffect(() => {
        if (selectedProductInfo && !qtyHasChanged) {
            setQty(`${selectedProductInfo.qtyOrdered}`);
        }
        // Xng-Change
        setSelectedProductId(selectedProductInfo ? selectedProductInfo.productId : "");
    }, [selectedProductInfo]);

    useEffect(() => {
        switch (errorType) {
            case "productIsConfigurable":
                setErrorMessage(productIsConfigurableMessage || translate("Cannot select configurable products"));
                break;
            case "productIsUnavailable":
                setErrorMessage(productIsUnavailableMessage || translate("Product is unavailable"));
                break;
            default:
                setErrorMessage("");
                break;
        }
    }, [errorType]);

    const onSelectionChangeHandler = (value?: string) => {
        if (searchResults && searchResults.length > 0) {
            const variantParentId = searchResults.find(o => o.id === value)?.styleParentId;
            if (variantParentId) {
                setProduct({
                    productId: variantParentId,
                    variantId: value,
                    validateProduct: true,
                    skipInventoryValidation,
                });
            } else {
                setProduct({
                    productId: value,
                    validateProduct: true,
                    skipInventoryValidation,
                });
            }
        }
    };

    const debouncedSearchProducts = debounce((query: string) => {
        searchProducts({ query });
    }, 300);

    const onInputChanged = (event: ChangeEvent<HTMLInputElement>) => {
        setErrorMessage("");
        setSearchTerm(event.target.value);

        if (selectedProductInfo) {
            clearProduct();
        }

        debouncedSearchProducts(event.target.value);
    };

    const selectProduct = () => {
        if (!selectedProductInfo || errorMessage || !product) {
            return;
        }

        const newProductInfo = { ...selectedProductInfo };
        newProductInfo.qtyOrdered = Math.max(Number(qty) || 1, product.minimumOrderQty || 1);
        onSelectProduct(newProductInfo, product);

        clearProduct();
        setQty("1");
        setQtyHasChanged(false);
        searchProducts({ query: "" });
    };

    const uomChangeHandler = (unitOfMeasure: string) => {
        if (!selectedProductInfo) {
            return;
        }

        setUnitOfMeasure({ unitOfMeasure });
    };

    const onKeyPress = (event: React.KeyboardEvent) => {
        if (event.charCode === ENTER_KEY) {
            if (selectedProductInfo) {
                selectProduct();
            } else if (searchTerm && (!searchResults || searchResults.length === 0)) {
                setProduct({ searchTerm, skipInventoryValidation });
            }
        }
    };

    const onQtyChangeHandler = (qty: number) => {
        setQty(`${qty}`);
        setQtyHasChanged(true);
    };

    return (
        <StyledWrapper {...styles.container} data-test-selector="productSelector">
            <StyledWrapper {...styles.searchWrapper}>
                <DynamicDropdown
                    {...styles.searchDynamicDropdown}
                    label={translate("Search")}
                    onSelectionChange={onSelectionChangeHandler}
                    onInputChange={onInputChanged}
                    onKeyPress={onKeyPress}
                    filterOption={() => true}
                    isLoading={isSearching}
                    options={options}
                    error={errorMessage}
                    data-test-selector="productSelector_search"
                    // Xng-Change
                    selected={selectedProductId}
                />
            </StyledWrapper>
            <StyledWrapper {...styles.qtyWrapper}>
                <Typography as="label">{translate("QTY")}</Typography>
                <ProductContext.Provider
                    value={{
                        product: product || ({} as ProductModel),
                        productInfo: selectedProductInfo || ({} as ProductInfo),
                        onQtyOrderedChanged: onQtyChangeHandler,
                    }}
                >
                    <ProductQuantityOrdered
                        extendedStyles={{
                            wrapper: {
                                css: css`
                                    ${productQuantityOrderedStyles.wrapper?.css}
                                    button:last-child {
                                        border-right: 2px solid ${getColor("text")} !important;
                                    }

                                    margin-top: 3px;
                                    height: calc(100% - 25px);
                                    &&.disabled > * {
                                        opacity: 1;
                                    }
                                    &&& input {
                                        max-width: 70px;
                                        height: 31px;
                                        text-align: center;
                                        padding: 0 !important;
                                        width: auto;
                                        border-radius: 0;
                                        border: none !important;
                                        background: ${getColor("common.white")};
                                    }
                                `,
                            },
                        }}
                        data-test-selector="productSelector_qty"
                    />
                </ProductContext.Provider>
            </StyledWrapper>
            <StyledWrapper {...styles.unitOfMeasureWrapper}>
                {product && selectedProductInfo && (
                    <ProductContext.Provider
                        value={{ product, productInfo: selectedProductInfo, onUnitOfMeasureChanged: uomChangeHandler }}
                    >
                        <ProductUnitOfMeasureSelect
                            extendedStyles={styles.unitOfMeasureSelect}
                            data-test-selector="productSelector_uom"
                        />
                    </ProductContext.Provider>
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.buttonWrapper}>
                <Button
                    aria-label={translate("Select Product")}
                    {...styles.selectButton}
                    onClick={() => selectProduct()}
                    disabled={!selectedProductInfo || !!errorMessage || Number(qty) < 1}
                    data-test-selector="productSelector_selectProduct"
                    className="select-product"
                >
                    {selectButtonTitle || translate("Select Product")}
                </Button>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductSelector);
